import { apiClient } from '@shared/utils/api-client';
import { axiosInstance } from '@shared/utils/axios-instance';
import { compressImageFiles } from '@shared/utils/compress-image';
import type {
  Product,
  ProductCardResponse,
  CreateProductDto,
  SetDiscountDto,
  SetPopularDto,
  SetHiddenDto,
  SetOutOfStockDto,
  SetNewDto,
  FlowerType,
  AssemblyType,
  Color,
  ProductPickupStockFormRow,
  SellerPromotionListResponse,
  SellerSearchPreviewResponse,
  SellerCatalogCityOption,
  UpdateProductPromotionDto,
} from '../types';

export type ProductImportResult = {
  created: Array<{
    row: number;
    id: number;
    name: string;
    hidden: boolean;
    updated: boolean;
    warnings: string[];
  }>;
  failed: Array<{
    row: number;
    name: string;
    errors: string[];
  }>;
};

export const productsApi = {
  create: async (data: CreateProductDto, files?: File[]): Promise<Product> => {
    const formData = new FormData();
    formData.append('name', data.name);
    if (data.description) {
      formData.append('description', data.description);
    }
    formData.append('price', data.price.toString());
    // Добавляем все categoryIds (NestJS с Transform преобразует строку в массив если нужно)
    data.categoryIds.forEach((categoryId) => {
      formData.append('categoryIds', categoryId.toString());
    });
    const filledComposition = data.composition?.filter((item) => item.value != null && String(item.value).trim() !== '') ?? [];
    if (filledComposition.length > 0) {
      formData.append('composition', JSON.stringify(filledComposition));
    }
    if (data.keywords && data.keywords.length > 0) {
      data.keywords.forEach((keyword) => {
        formData.append('keywords', keyword);
      });
    }
    if (data.height !== undefined) {
      formData.append('height', data.height.toString());
    }
    if (data.width !== undefined) {
      formData.append('width', data.width.toString());
    }
    if (data.weight !== undefined) {
      formData.append('weight', data.weight.toString());
    }
    if (data.flowerCount !== undefined && data.flowerCount !== null) {
      formData.append('flowerCount', data.flowerCount.toString());
    }
    formData.append('isNotFlower', data.isNotFlower.toString());
    if (data.flowerTypeIds && data.flowerTypeIds.length > 0) {
      data.flowerTypeIds.forEach((flowerTypeId) => {
        formData.append('flowerTypeIds', flowerTypeId.toString());
      });
    }
    if (data.assemblyTypeId !== undefined) {
      formData.append('assemblyTypeId', data.assemblyTypeId.toString());
    }
    if (data.assemblyTimeMinutes !== undefined && data.assemblyTimeMinutes !== null && data.assemblyTimeMinutes > 0) {
      formData.append('assemblyTimeMinutes', data.assemblyTimeMinutes.toString());
    }
    if (data.colorIds && data.colorIds.length > 0) {
      data.colorIds.forEach((colorId) => {
        formData.append('colorIds', colorId.toString());
      });
    }
    if (data.pickupStocks && data.pickupStocks.length > 0) {
      formData.append('pickupStocks', JSON.stringify(data.pickupStocks));
    }
    if (data.variantInfo) {
      formData.append('variantInfo[variantOfProductId]', data.variantInfo.variantOfProductId.toString());
      // variantOfProductName и variantName больше не используются - имя варианта устанавливается автоматически
      if (data.variantInfo.variantOfProductName) {
        formData.append('variantInfo[variantOfProductName]', data.variantInfo.variantOfProductName);
      }
      if (data.variantInfo.variantName) {
        formData.append('variantInfo[variantName]', data.variantInfo.variantName);
      }
    }
    if (files && files.length > 0) {
      const compressed = await compressImageFiles(files);
      compressed.forEach((file) => {
        formData.append('images', file);
      });
    }
    return apiClient.post<Product>('/products', formData);
  },

  update: async (id: number, data: Partial<CreateProductDto>, files?: File[], deletedImageIds?: number[]): Promise<Product> => {
    const formData = new FormData();
    if (data.name) {
      formData.append('name', data.name);
    }
    if (data.description !== undefined) {
      formData.append('description', data.description);
    }
    if (data.price !== undefined) {
      formData.append('price', data.price.toString());
    }
    if (data.categoryIds && data.categoryIds.length > 0) {
      data.categoryIds.forEach((categoryId) => {
        formData.append('categoryIds', categoryId.toString());
      });
    }
    if (data.composition !== undefined) {
      const filledComposition = (Array.isArray(data.composition) ? data.composition : []).filter(
        (item) => item.value != null && String(item.value).trim() !== ''
      );
      formData.append('composition', JSON.stringify(filledComposition));
    }
    if (data.keywords !== undefined) {
      if (data.keywords.length > 0) {
        data.keywords.forEach((keyword) => {
          formData.append('keywords', keyword);
        });
      }
    }
    if (data.height !== undefined) {
      formData.append('height', data.height.toString());
    }
    if (data.width !== undefined) {
      formData.append('width', data.width.toString());
    }
    if (data.weight !== undefined) {
      formData.append('weight', data.weight.toString());
    }
    if (data.flowerCount !== undefined) {
      formData.append('flowerCount', data.flowerCount !== null ? data.flowerCount.toString() : '');
    }
    if (data.isNotFlower !== undefined) {
      formData.append('isNotFlower', data.isNotFlower.toString());
    }
    if (data.flowerTypeIds !== undefined) {
      if (data.flowerTypeIds.length > 0) {
        data.flowerTypeIds.forEach((flowerTypeId) => {
          formData.append('flowerTypeIds', flowerTypeId.toString());
        });
      }
    }
    if (data.assemblyTypeId !== undefined) {
      formData.append('assemblyTypeId', data.assemblyTypeId !== null ? data.assemblyTypeId.toString() : '');
    }
    if (data.assemblyTimeMinutes !== undefined) {
      formData.append(
        'assemblyTimeMinutes',
        data.assemblyTimeMinutes != null && data.assemblyTimeMinutes > 0
          ? data.assemblyTimeMinutes.toString()
          : '',
      );
    }
    if (data.colorIds !== undefined) {
      if (data.colorIds.length > 0) {
        data.colorIds.forEach((colorId) => {
          formData.append('colorIds', colorId.toString());
        });
      }
    }
    if (data.cpcRub !== undefined && data.cpcRub !== null) {
      formData.append('cpcRub', data.cpcRub.toString());
    }
    if (data.promotionEnabled !== undefined) {
      formData.append('promotionEnabled', String(data.promotionEnabled));
    }
    if (files && files.length > 0) {
      const compressed = await compressImageFiles(files);
      compressed.forEach((file) => {
        formData.append('images', file);
      });
    }
    if (deletedImageIds && deletedImageIds.length > 0) {
      deletedImageIds.forEach((imageId) => {
        formData.append('deletedImageIds', imageId.toString());
      });
    }
    return apiClient.patch<Product>(`/products/${id}`, formData);
  },

  /** Только остатки по точкам — не трогает модерацию товара */
  updatePickupStocks: async (id: number, pickupStocks: ProductPickupStockFormRow[]): Promise<Product> => {
    return apiClient.patch<Product>(`/products/${id}/pickup-stocks`, { pickupStocks });
  },

  getSellerPromotion: async (): Promise<SellerPromotionListResponse> => {
    return apiClient.get<SellerPromotionListResponse>('/products/seller/promotion');
  },

  getSellerPromotionRankPending: async (): Promise<{ productIds: number[] }> => {
    return apiClient.get<{ productIds: number[] }>(
      '/products/seller/promotion/rank-pending',
    );
  },

  getSellerSearchPreview: async (params: {
    keyword?: string;
    categoryId?: number;
    cityKey?: string;
  }): Promise<SellerSearchPreviewResponse> => {
    const search = new URLSearchParams();
    if (params.keyword?.trim()) {
      search.set('keyword', params.keyword.trim());
    }
    if (params.categoryId != null) {
      search.set('categoryId', String(params.categoryId));
    }
    if (params.cityKey) {
      search.set('cityKey', params.cityKey);
    }
    return apiClient.get<SellerSearchPreviewResponse>(
      `/products/seller/promotion/search-preview${search.toString() ? `?${search.toString()}` : ''}`,
    );
  },

  getSellerPromotionCities: async (): Promise<{
    cities: SellerCatalogCityOption[];
  }> => {
    return apiClient.get<{ cities: SellerCatalogCityOption[] }>(
      '/products/seller/promotion/cities',
    );
  },

  updatePromotion: async (
    id: number,
    data: UpdateProductPromotionDto,
  ): Promise<{ ok: boolean }> => {
    return apiClient.patch<{ ok: boolean }>(`/products/${id}/promotion`, data);
  },

  getAll: async (filters?: {
    categoryId?: number;
    colorIds?: number[];
    flowerTypeIds?: number[];
    assemblyTypeIds?: number[];
    minPrice?: number;
    maxPrice?: number;
    sortBy?: string;
  }): Promise<ProductCardResponse[]> => {
    if (!filters) {
      return apiClient.get<ProductCardResponse[]>('/products');
    }
    const params = new URLSearchParams();
    if (filters.categoryId) params.append('categoryId', filters.categoryId.toString());
    if (filters.colorIds && filters.colorIds.length > 0) {
      filters.colorIds.forEach(id => params.append('colorIds', id.toString()));
    }
    if (filters.flowerTypeIds && filters.flowerTypeIds.length > 0) {
      filters.flowerTypeIds.forEach(id => params.append('flowerTypeIds', id.toString()));
    }
    if (filters.assemblyTypeIds && filters.assemblyTypeIds.length > 0) {
      filters.assemblyTypeIds.forEach(id => params.append('assemblyTypeIds', id.toString()));
    }
    if (filters.minPrice !== undefined) params.append('minPrice', filters.minPrice.toString());
    if (filters.maxPrice !== undefined) params.append('maxPrice', filters.maxPrice.toString());
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    const queryString = params.toString();
    const url = queryString ? `/products?${queryString}` : '/products';
    return apiClient.get<ProductCardResponse[]>(url);
  },

  /** Категории, в которых у продавца есть товары (кабинет продавца). */
  getSellerCategories: async (): Promise<Array<{ id: number; name: string; slug: string }>> => {
    return apiClient.get<Array<{ id: number; name: string; slug: string }>>('/products/seller/categories');
  },

  getById: async (id: number): Promise<Product> => {
    return apiClient.get<Product>(`/products/${id}`);
  },

  setDiscount: async (id: number, data: SetDiscountDto): Promise<Product> => {
    return apiClient.patch<Product>(`/products/${id}/discount`, data);
  },

  setFlag: async (id: number, type: string, value: boolean): Promise<Product> => {
    return apiClient.patch<Product>(`/products/${id}/flag/${type}`, { value });
  },

  // Оставляем старые методы для обратной совместимости
  setPopular: async (id: number, data: SetPopularDto): Promise<Product> => {
    return apiClient.patch<Product>(`/products/${id}/flag/popular`, { value: data.isPopular });
  },

  setHidden: async (id: number, data: SetHiddenDto): Promise<Product> => {
    return apiClient.patch<Product>(`/products/${id}/flag/hidden`, { value: data.isHidden });
  },

  setOutOfStock: async (id: number, data: SetOutOfStockDto): Promise<Product> => {
    return apiClient.patch<Product>(`/products/${id}/flag/out-of-stock`, { value: data.isOutOfStock });
  },

  setNew: async (id: number, data: SetNewDto): Promise<Product> => {
    return apiClient.patch<Product>(`/products/${id}/flag/new`, { value: data.isNew });
  },

  delete: async (id: number): Promise<void> => {
    return apiClient.delete(`/products/${id}`);
  },

  getPopular: async (): Promise<ProductCardResponse[]> => {
    return apiClient.get<ProductCardResponse[]>('/products/collections/popular');
  },

  getHidden: async (): Promise<ProductCardResponse[]> => {
    return apiClient.get<ProductCardResponse[]>('/products/collections/hidden');
  },

  getDiscount: async (): Promise<ProductCardResponse[]> => {
    return apiClient.get<ProductCardResponse[]>('/products/collections/discount');
  },

  getOutOfStock: async (): Promise<ProductCardResponse[]> => {
    return apiClient.get<ProductCardResponse[]>('/products/collections/out-of-stock');
  },

  getNew: async (): Promise<ProductCardResponse[]> => {
    return apiClient.get<ProductCardResponse[]>('/products/collections/new');
  },

  getCollection: async (
    type: string,
    filters?: {
      categoryId?: number;
      colorIds?: number[];
      flowerTypeIds?: number[];
      assemblyTypeIds?: number[];
      flowerCounts?: number[];
      minPrice?: number;
      maxPrice?: number;
      sortBy?: string;
    },
  ): Promise<ProductCardResponse[]> => {
    if (!filters) {
      return apiClient.get<ProductCardResponse[]>(`/products/collections/${type}`);
    }
    const params = new URLSearchParams();
    if (filters.categoryId) params.append('categoryId', filters.categoryId.toString());
    if (filters.colorIds && filters.colorIds.length > 0) {
      filters.colorIds.forEach(id => params.append('colorIds', id.toString()));
    }
    if (filters.flowerTypeIds && filters.flowerTypeIds.length > 0) {
      filters.flowerTypeIds.forEach(id => params.append('flowerTypeIds', id.toString()));
    }
    if (filters.assemblyTypeIds && filters.assemblyTypeIds.length > 0) {
      filters.assemblyTypeIds.forEach(id => params.append('assemblyTypeIds', id.toString()));
    }
    if (filters.flowerCounts && filters.flowerCounts.length > 0) {
      filters.flowerCounts.forEach(count => params.append('flowerCounts', count.toString()));
    }
    if (filters.minPrice !== undefined) params.append('minPrice', filters.minPrice.toString());
    if (filters.maxPrice !== undefined) params.append('maxPrice', filters.maxPrice.toString());
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    const queryString = params.toString();
    const url = queryString ? `/products/collections/${type}?${queryString}` : `/products/collections/${type}`;
    return apiClient.get<ProductCardResponse[]>(url);
  },

  getCollectionsCounts: async (): Promise<{
    popular: number;
    hidden: number;
    discount: number;
    outOfStock: number;
    new: number;
  }> => {
    return apiClient.get<{
      popular: number;
      hidden: number;
      discount: number;
      outOfStock: number;
      new: number;
    }>('/products/collections/counts');
  },

  getFlowerTypes: async (): Promise<FlowerType[]> => {
    return apiClient.get<FlowerType[]>('/products/options/flower-types');
  },

  getAssemblyTypes: async (): Promise<AssemblyType[]> => {
    return apiClient.get<AssemblyType[]>('/products/options/assembly-types');
  },

  getColors: async (): Promise<Color[]> => {
    return apiClient.get<Color[]>('/products/options/colors');
  },

  // Color methods
  createColor: async (data: { name: string; hexCode?: string }): Promise<Color> => {
    return apiClient.post<Color>('/products/options/colors', data);
  },

  updateColor: async (id: number, data: { name?: string; hexCode?: string }): Promise<Color> => {
    return apiClient.patch<Color>(`/products/options/colors/${id}`, data);
  },

  deleteColor: async (id: number): Promise<void> => {
    return apiClient.delete<void>(`/products/options/colors/${id}`);
  },

  // FlowerType methods
  createFlowerType: async (data: { name: string }): Promise<FlowerType> => {
    return apiClient.post<FlowerType>('/products/options/flower-types', data);
  },

  updateFlowerType: async (id: number, data: { name?: string }): Promise<FlowerType> => {
    return apiClient.patch<FlowerType>(`/products/options/flower-types/${id}`, data);
  },

  deleteFlowerType: async (id: number): Promise<void> => {
    return apiClient.delete<void>(`/products/options/flower-types/${id}`);
  },

  // AssemblyType methods
  createAssemblyType: async (data: { name: string }): Promise<AssemblyType> => {
    return apiClient.post<AssemblyType>('/products/options/assembly-types', data);
  },

  updateAssemblyType: async (id: number, data: { name?: string }): Promise<AssemblyType> => {
    return apiClient.patch<AssemblyType>(`/products/options/assembly-types/${id}`, data);
  },

  deleteAssemblyType: async (id: number): Promise<void> => {
    return apiClient.delete<void>(`/products/options/assembly-types/${id}`);
  },

  // Массовые операции
  bulkSetFlag: async (type: string, productIds: number[], value: boolean): Promise<{ count: number }> => {
    return apiClient.post<{ count: number }>(`/products/bulk/flag/${type}`, { productIds, value });
  },

  // Оставляем старые методы для обратной совместимости
  bulkSetPopular: async (productIds: number[], isPopular: boolean): Promise<{ count: number }> => {
    return apiClient.post<{ count: number }>('/products/bulk/flag/popular', { productIds, value: isPopular });
  },

  bulkSetHidden: async (productIds: number[], isHidden: boolean): Promise<{ count: number }> => {
    return apiClient.post<{ count: number }>('/products/bulk/flag/hidden', { productIds, value: isHidden });
  },

  bulkSetOutOfStock: async (productIds: number[], isOutOfStock: boolean): Promise<{ count: number }> => {
    return apiClient.post<{ count: number }>('/products/bulk/flag/out-of-stock', { productIds, value: isOutOfStock });
  },

  bulkSetNew: async (productIds: number[], isNew: boolean): Promise<{ count: number }> => {
    return apiClient.post<{ count: number }>('/products/bulk/flag/new', { productIds, value: isNew });
  },

  bulkDelete: async (productIds: number[]): Promise<void> => {
    return apiClient.post<void>('/products/bulk/delete', { productIds });
  },

  bulkAddToCategory: async (productIds: number[], categoryId: number): Promise<{ success: boolean; count: number }> => {
    return apiClient.post<{ success: boolean; count: number }>('/products/bulk/add-to-category', { productIds, categoryId });
  },

  removeFromCategory: async (productId: number, categoryId: number): Promise<{ success: boolean }> => {
    return apiClient.delete<{ success: boolean }>(`/products/${productId}/category/${categoryId}`);
  },

  updateProductImagesOrder: async (productId: number, items: { id: number; order: number }[]): Promise<Product> => {
    return apiClient.patch<Product>(`/products/${productId}/images/order`, { items });
  },

  createVariant: async (data: { products: Array<{ productId: number }> }): Promise<{ success: boolean; variantId: number; message: string }> => {
    return apiClient.post<{ success: boolean; variantId: number; message: string }>('/products/variants', data);
  },

  updateVariantName: async (productId: number, variantName: string): Promise<{ success: boolean; message: string }> => {
    return apiClient.patch<{ success: boolean; message: string }>(`/products/${productId}/variant-name`, { variantName });
  },

  bulkUpdateVariantName: async (updates: Array<{ productId: number; variantName: string }>): Promise<{ success: boolean; count: number; total: number; results: Array<{ productId: number; success: boolean; message: string }>; message: string }> => {
    return apiClient.post<{ success: boolean; count: number; total: number; results: Array<{ productId: number; success: boolean; message: string }>; message: string }>('/products/variants/bulk-update-variant-name', { updates });
  },

  removeVariant: async (variantId: number): Promise<{ success: boolean; message: string }> => {
    return apiClient.delete<{ success: boolean; message: string }>(`/products/variants/remove/${variantId}`);
  },

  disbandVariantGroup: async (productId: number): Promise<{ success: boolean; message: string }> => {
    return apiClient.delete<{ success: boolean; message: string }>(`/products/${productId}/variant-group`);
  },

  downloadImportTemplate: async (): Promise<void> => {
    const response = await axiosInstance.get<Blob>('/products/import/template', {
      responseType: 'blob',
      timeout: 60_000,
    });
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'import-tovarov.xlsx';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },

  importFromExcel: async (file: File): Promise<ProductImportResult> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axiosInstance.post<ProductImportResult>('/products/import', formData, {
      timeout: 180_000,
    });
    return response.data;
  },
};

